import { inject, ref, readonly, watch } from "vue";
import { PAGE_APP_SYMBOL } from "_Main/injectionSymbols";
import { PROJECT_FORM_SAVE_STATE, PROJECT_FORM_TYPES } from "Forms/Components/ProjectForm/projectFormStore";
import { PROJECT_FORM_CONTROLLER_TYPES } from "Forms/Components/ProjectForm/projectFormControllerStore";
import { useStore } from "vuex";
import { storeToRefs } from "pinia";

export function useDebounceSaveFormData(editProjectBase) {
    const INITIAL_DELAY = 5000;
    const SECONDARY_DELAY = 1000;

    const timeoutHandle = ref(null);
    const startTime = ref(null);
    const actionLog = ref([]);

    const app = inject(PAGE_APP_SYMBOL);
    const { duringSubmit, projectSaveState, inAdminMode } = storeToRefs(editProjectBase);
    const { dispatchSaveFormData } = editProjectBase;

    watch(projectSaveState, (value) => {
        if (value === PROJECT_FORM_SAVE_STATE.HasUnsaved) {
            app.setUnsavedChanges(true);
        } else if (value === PROJECT_FORM_SAVE_STATE.Saved) {
            app.setUnsavedChanges(false);
        }
    });

    // Register with the vuex store and listen for any form actions
    const store = useStore();
    store.subscribeAction({ before: onBeforeAction, after: onAfterAction });

    function onBeforeAction(action) {
        if (action.type === PROJECT_FORM_CONTROLLER_TYPES.actions.submitProject) {
            cancelDebouncedSave();
        }

        // Update the action log
        let actionType = action.type;
        if (
            action.type === PROJECT_FORM_CONTROLLER_TYPES.actions.saveFormData &&
            action.payload &&
            action.payload.fromDebounce
        ) {
            actionType += "/fromDebounce";
        }
        const now = new Date();
        const timeStr = now.toTimeString();
        const newLog = `${timeStr} - ${actionType}`;
        if (actionLog.value.length <= 0 || actionLog.value[actionLog.value.length - 1] !== newLog) {
            actionLog.value.push(newLog);
        }
    }

    function onAfterAction(action) {
        if (
            !inAdminMode.value &&
            action.type === PROJECT_FORM_TYPES.actions.setProjectData &&
            projectSaveState.value === PROJECT_FORM_SAVE_STATE.HasUnsaved &&
            !duringSubmit.value
        ) {
            queueDebouncedSave();
        }
    }

    /**
     * We want to delay saving for some amount of time so that we can minimize server calls.
     * At the same time, if the user has been actively making changes and seeing the "saving..."
     * message, we don't want to wait too long before actually making the call. So here is what we do:
     *  1. When we first register a save, we delay the server call for 5 secs and make note of the time
     *  2. For the first 5 secs, we continue to debounce, each time making the debounce timeout be
     *      5 secs from the first start time.
     *  3. If we continue debouncing after 5secs, reduce the timeout to a min of 1 sec
     */
    function queueDebouncedSave() {
        if (!startTime.value) {
            startTime.value = Date.now();
        }
        const totalDelay = Date.now() - startTime.value;
        const delay = Math.max(SECONDARY_DELAY, INITIAL_DELAY - totalDelay);

        cancelDebouncedSave();

        timeoutHandle.value = setTimeout(async () => {
            timeoutHandle.value = null;
            startTime.value = null;
            await dispatchSaveFormData({ fromDebounce: true });
        }, delay);
    }

    async function saveFormData() {
        if (timeoutHandle.value) {
            cancelDebouncedSave();
            await dispatchSaveFormData();
        }
    }

    function cancelDebouncedSave() {
        if (timeoutHandle.value) {
            clearTimeout(timeoutHandle.value);
            timeoutHandle.value = null;
        }
    }

    return {
        saveFormData,
        actionLog: readonly(actionLog)
    };
}
